import { TipQueueEntry, TipQueueStatus } from "../../types/TipQueue";
import { timeAgo } from "../pages/broadcasters/community/format";

// One tip in the queue, with its actions. Shared by the Tip Queue page and
// a tipper's profile, so a tip reads and behaves the same wherever it shows.

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : "";

export type Lane = "waiting" | "banked";

export type Handlers = {
  busyId: string | null;
  onStatus: (entry: TipQueueEntry, status: TipQueueStatus) => void;
  onMove: (lane: Lane, entry: TipQueueEntry, direction: -1 | 1) => void;
};

export const EntryCard = ({
  entry,
  lane,
  index,
  count,
  handlers,
  canReorder = true,
  readOnly = false,
}: {
  entry: TipQueueEntry;
  lane: Lane | "done";
  index: number;
  count: number;
  handlers?: Handlers;
  // Off where the list isn't a lane a mod orders (a tipper's profile).
  canReorder?: boolean;
  // No actions at all: the tipper looking at their own tips.
  readOnly?: boolean;
}) => {
  const busy = handlers?.busyId === entry.id;
  const act = handlers ?? { busyId: null, onStatus: () => {}, onMove: () => {} };
  return (
    <li
      className={`rounded-box border p-3 flex flex-col gap-2 ${
        lane === "banked"
          ? "border-accent/60 bg-accent/5"
          : lane === "done"
            ? "border-base-300 opacity-70"
            : "border-success/60 bg-success/5"
      }`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-semibold break-all">{entry.username}</span>
        <span
          className={`font-semibold ${
            lane === "banked" ? "text-accent" : "text-success"
          }`}
        >
          {entry.itemName}
        </span>
        <span className="text-xs text-base-content/60">
          {entry.tokens.toLocaleString()} tokens
        </span>
        {entry.source === "manual" && (
          <span
            className="badge badge-outline badge-xs"
            title={entry.addedBy ? `Added by ${entry.addedBy}` : "Added by a mod"}
          >
            added by {entry.addedBy ?? "a mod"}
          </span>
        )}
      </div>
      {entry.message && (
        <div className="text-sm bg-base-200 rounded px-2 py-1 break-words">
          {entry.message}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 text-xs text-base-content/60">
        <span title={when(entry.createdAt)}>{timeAgo(entry.createdAt)}</span>
        {lane === "banked" && entry.bankedBy && (
          <span title={when(entry.bankedAt)}>
            {entry.bankedBy === "menu"
              ? "auto-banked (menu item)"
              : `banked by ${entry.bankedBy}`}
          </span>
        )}
        {lane === "done" && entry.completedBy && (
          <span title={when(entry.completedAt)}>
            done by {entry.completedBy}
            {entry.completedAt ? `, ${timeAgo(entry.completedAt)}` : ""}
          </span>
        )}
      </div>
      {!readOnly && (
      <div className="flex flex-wrap gap-1">
        {lane !== "done" && (
          <>
            {canReorder && (
            <button
              className="btn btn-ghost btn-xs"
              disabled={busy || index === 0}
              onClick={() => act.onMove(lane, entry, -1)}
              aria-label="Move up"
              title="Move up"
            >
              ▲
            </button>
            )}
            {canReorder && (
            <button
              className="btn btn-ghost btn-xs"
              disabled={busy || index === count - 1}
              onClick={() => act.onMove(lane, entry, 1)}
              aria-label="Move down"
              title="Move down"
            >
              ▼
            </button>
            )}
            <span className="flex-1" />
            {lane === "waiting" ? (
              <button
                className="btn btn-outline btn-accent btn-xs"
                disabled={busy}
                title="Save it for another time"
                onClick={() => act.onStatus(entry, "banked")}
              >
                Bank
              </button>
            ) : (
              <button
                className="btn btn-outline btn-xs"
                disabled={busy}
                title="Back to this show's list"
                onClick={() => act.onStatus(entry, "waiting")}
              >
                Unbank
              </button>
            )}
            <button
              className="btn btn-success btn-xs"
              disabled={busy}
              onClick={() => act.onStatus(entry, "done")}
            >
              Done
            </button>
          </>
        )}
        {lane === "done" && (
          <button
            className="btn btn-outline btn-xs"
            disabled={busy}
            onClick={() => act.onStatus(entry, "waiting")}
          >
            Restore
          </button>
        )}
      </div>
      )}
    </li>
  );
};

